import { faBadgeDollar } from '@fortawesome/pro-light-svg-icons';
import { Action, AmountLineItem, Badge, Card, Icon } from 'corso-admin-ui';
import {
  CrewClaimResolutionMethodEnum,
  crewClaimTypeEnumName,
  isTruthy,
} from 'corso-types';
import { Key, useState } from 'react';
import {
  ClaimReview,
  isRequestedAs,
  isUndecided,
  useClaim,
} from '~/features/claimReview/ClaimReviewProvider';
import EditClaimFee, {
  useShowEditClaimFee,
} from '~/features/claimReview/components/EditClaimFee';
import { ExchangeOrderLine } from '~/components/lineItems/ExchangeOrderLine';
import { FeeLineItem } from '~/components/lineItems/FeeLineItem';
import { GiftCardLineItem } from '~/components/lineItems/GiftCardLineItem';
import { RefundLineItem } from '~/components/lineItems/RefundLineItem';
import { List, ListItem } from './ClaimSummaryList';
import { PaymentLineItem } from './PaymentLineItem';
import WarrantyReviewLine from './WarrantyReviewLine';

type UnifiedSummaryLineItem = {
  id: Key;
  name: string | null;
  imgUrl: string | null;
  quantity: number;
  optionsFromPlatform: {
    value: string;
    name: string;
  }[];
  unitPrice: number | null;
  unitTax: number | null;
  sku?: string | null;
};

type UnifiedFee = {
  id: Key;
  kind: 'shipping' | 'handling';
  name: string;
  amount: number;
};

type UnifiedSummary = {
  exchangeOrders: {
    id: Key;
    lineItems: UnifiedSummaryLineItem[];
    orderNameFromPlatform?: string;
    containsShopNowItems: boolean;
    platformAdminUrl?: string;
    wasResolvedExternally: boolean;
    isOriginalOrder: boolean;
  }[];
  giftCards: {
    id: Key;
    amount: number;
    incentiveAmount: number;
    orderNameFromPlatform?: string;
    platformAdminUrl?: string;
    wasResolvedExternally: boolean;
  }[];
  refunds: {
    id: Key;
    amount: number;
    wasResolvedExternally: boolean;
  }[];
  warrantyReviews: {
    id: Key;
    lineItems: UnifiedSummaryLineItem[];
  }[];
  repairs: {
    id: Key;
    lineItems: UnifiedSummaryLineItem[];
  }[];
  fees: UnifiedFee[];
};

// * warranty review line items

function unifiedSummary(claim: ClaimReview) {
  // ? maybe the shape of both summaries could be the same from the backend
  const {
    requestSummary,
    resolutionSummary,
    claimLineItems,
    wasOriginalOrderModified,
    originalStoreOrder: { taxRate },
  } = claim;

  const claimContainsShopNowItems = claimLineItems.some(
    (item) => item.isShopNow,
  );

  // * fees are the same between request and resolution summaries, extracted from the claim itself
  const fees: UnifiedFee[] = [];

  claimLineItems.forEach((cli) => {
    (cli.fees ?? []).forEach((fee) => {
      fees.push({
        id: `line-item-fee-${fee.id}`,
        kind: 'handling',
        name: fee.displayName,
        amount: fee.amount,
      });
    });
  });

  if (claim.returnShippingAmountApplied) {
    fees.push({
      id: 'request-return-shipping-fee',
      kind: 'shipping',
      name: 'Return Shipping Fee',
      amount: claim.returnShippingAmountApplied,
    });
  }

  if (claim.exchangeOrderShippingAmountApplied) {
    fees.push({
      id: 'request-exchange-order-shipping-fee',
      kind: 'shipping',
      name: 'Exchange Order Shipping Fee',
      amount: claim.exchangeOrderShippingAmountApplied,
    });
  }

  if (claim.feeApplied) {
    fees.push({
      id: 'request-handling-fee',
      kind: 'handling',
      name: claim.feeName ?? 'Handling Fee',
      amount: claim.feeApplied,
    });
  }

  if (claim.shopNowIncentiveAmountApplied) {
    fees.push({
      id: 'request-shop-now-incentive',
      kind: 'handling',
      name: 'Shop Now Incentive',
      amount: claim.shopNowIncentiveAmountApplied,
    });
  }

  // ? we want to show the repairs with the request or resolution summary
  const repairs: UnifiedSummary['repairs'] = [];
  const repairLineItems = claimLineItems
    .filter(
      (cli) =>
        cli.resolutionMethodEnum === CrewClaimResolutionMethodEnum.repair,
    )
    .map((claimLineItem) => ({
      id: `repair-line-item-${claimLineItem.id}`,
      name: claimLineItem.originalStoreOrderLineItem.name ?? null,
      imgUrl: claimLineItem.originalStoreOrderLineItem.imgUrl ?? null,
      quantity: claimLineItem.originalStoreOrderLineItem.quantity,
      optionsFromPlatform:
        claimLineItem.originalStoreOrderLineItem.optionsFromPlatform,
      unitPrice: claimLineItem.unitPrice,
      unitTax: claimLineItem.unitTax,
      sku: claimLineItem.originalStoreOrderLineItem.sku ?? null,
    })) satisfies UnifiedSummaryLineItem[];

  if (repairLineItems.length > 0) {
    repairs.push({
      id: 'repair',
      lineItems: repairLineItems,
    });
  }

  // use resolution summary if it exists with data; otherwise, show request summary
  if (
    resolutionSummary &&
    Object.values(resolutionSummary).some((v) => v.length > 0)
  ) {
    return {
      exchangeOrders: resolutionSummary.exchangeOrders.map((order) => ({
        ...order,
        containsShopNowItems: !!order.wasShopNow,
        lineItems: order.lineItems.map((lineItem) => ({
          ...lineItem,
          // * hiding zeros from summary, as the resolution often has zero-dollar orders
          unitPrice: lineItem.unitPrice !== 0 ? lineItem.unitPrice : null,
          unitTax: lineItem.unitTax !== 0 ? lineItem.unitTax : null,
        })),
      })),
      // ? when using the resolution summary, the amount doesn't include the incentive.. adding it back here so it's consistent with the request summary
      giftCards: resolutionSummary.giftCards.map((rs) => {
        const amount = rs.amount + rs.incentiveAmount;
        return {
          ...rs,
          amount,
        };
      }),
      refunds: resolutionSummary.refunds,
      fees,
      warrantyReviews: [], // no warranty review present in resolution summary
      repairs,
    } satisfies UnifiedSummary;
  }

  const exchangeOrders: UnifiedSummary['exchangeOrders'] = [];
  const refunds: UnifiedSummary['refunds'] = [];
  const giftCards: UnifiedSummary['giftCards'] = [];

  const variantExchangeLineItems = (requestSummary?.approvedLineItems ?? [])
    .filter((approval) => approval.resolutionMethodEnum === 'Variant_Exchange')
    .flatMap((approval) => {
      const claimLineItem = claimLineItems.find(({ id }) => id === approval.id);
      if (!claimLineItem?.variantExchangeLineItem) return null;

      const unitTax =
        (taxRate ?? 0) * claimLineItem.variantExchangeLineItem.price;
      return {
        id: `request-variant-line-item-${claimLineItem.id}`,
        name: claimLineItem.variantExchangeLineItem.title ?? null,
        imgUrl: claimLineItem.variantExchangeLineItem.imgUrl ?? null,
        quantity: approval.quantity, // should equal the line item quantity
        optionsFromPlatform:
          claimLineItem.variantExchangeLineItem.options ?? [],
        unitPrice: claimLineItem.variantExchangeLineItem.price,
        unitTax,
        sku: claimLineItem.variantExchangeLineItem.sku,
      } satisfies UnifiedSummaryLineItem;
    })
    .filter(isTruthy);

  const replacementOrderLineItems = (requestSummary?.approvedLineItems ?? [])
    .filter((approval) => approval.resolutionMethodEnum === 'Replacement_Order')
    .flatMap((approval) => {
      const claimLineItem = claimLineItems.find(({ id }) => id === approval.id);
      if (!claimLineItem?.replacementItems?.length) return null;

      return claimLineItem.replacementItems.map((replacementItem, index) => {
        const unitTax = (taxRate ?? 0) * replacementItem.price;
        return {
          id: `request-replacement-line-item-${claimLineItem.id}-${index}`,
          name: replacementItem.title ?? null,
          imgUrl: replacementItem.imgUrl ?? null,
          quantity: replacementItem.quantity, // should equal the line item quantity
          optionsFromPlatform: replacementItem.options ?? [],
          unitPrice: replacementItem.price,
          unitTax,
          sku: replacementItem.sku,
        } satisfies UnifiedSummaryLineItem;
      });
    })
    .filter(isTruthy);

  if (variantExchangeLineItems.length > 0) {
    exchangeOrders.push({
      id: 'request-variant-exchange-order',
      lineItems: variantExchangeLineItems,
      containsShopNowItems: false,
      wasResolvedExternally: false,
      isOriginalOrder: wasOriginalOrderModified,
    });
  }

  if (replacementOrderLineItems.length > 0) {
    exchangeOrders.push({
      id: 'request-replacement-order',
      lineItems: replacementOrderLineItems,
      containsShopNowItems: claimContainsShopNowItems,
      wasResolvedExternally: false,
      isOriginalOrder: wasOriginalOrderModified,
    });
  }

  if (requestSummary?.totals.giftCard) {
    giftCards.push({
      id: 'request-gift-card',
      amount: requestSummary.totals.giftCard,
      incentiveAmount: claim.giftCardIncentiveAmountApplied,
      wasResolvedExternally: false,
    });
  }

  if (requestSummary?.totals.refund) {
    refunds.push({
      id: 'request-refund',
      amount: requestSummary.totals.refund,
      wasResolvedExternally: false,
    });
  }

  const warrantyReviews: UnifiedSummary['warrantyReviews'] = [];

  const warrantyReviewLineItems = claimLineItems
    .filter(
      (claimLineItem) =>
        isRequestedAs(
          claimLineItem,
          CrewClaimResolutionMethodEnum.warrantyReview,
        ) && isUndecided(claimLineItem),
    )
    .map((claimLineItem) => ({
      id: `warranty-review-line-item-${claimLineItem.id}`,
      name: claimLineItem.originalStoreOrderLineItem.name ?? null,
      imgUrl: claimLineItem.originalStoreOrderLineItem.imgUrl ?? null,
      quantity: claimLineItem.quantity,
      optionsFromPlatform:
        claimLineItem.originalStoreOrderLineItem.optionsFromPlatform,
      unitPrice: claimLineItem.unitPrice,
      unitTax: claimLineItem.unitTax,
      sku: claimLineItem.originalStoreOrderLineItem.sku ?? null,
    })) satisfies UnifiedSummaryLineItem[];

  if (warrantyReviewLineItems.length > 0) {
    warrantyReviews.push({
      id: 'warranty-review',
      lineItems: warrantyReviewLineItems,
    });
  }

  return {
    repairs,
    exchangeOrders,
    giftCards,
    refunds,
    fees,
    warrantyReviews,
  } satisfies UnifiedSummary;
}

function NothingIssued() {
  return (
    <div className="m-2 flex items-center gap-x-2">
      <Icon icon="info" />
      <p className="text-sm">Nothing issued for this request.</p>
    </div>
  );
}

export default function ClaimSummary() {
  const claim = useClaim();
  const [showFeeForm, setShowFeeForm] = useState(false);

  const showEditClaimFee = useShowEditClaimFee();

  const summary = unifiedSummary(claim);

  const summaryHasItems = Object.values(summary).some(
    (item) => item.length > 0,
  );

  const showMoneySection =
    summary.refunds.length > 0 ||
    summary.giftCards.length > 0 ||
    summary.fees.length > 0 ||
    claim.customerPayment;

  return (
    <Card flush>
      <div className="p-4 pb-0">
        <Card.Line>
          <Card.Heading>
            {crewClaimTypeEnumName[claim.claimType]} Summary
          </Card.Heading>
        </Card.Line>
      </div>
      {!summaryHasItems && (
        <div className="p-4 pt-0">
          <NothingIssued />
        </div>
      )}
      {summaryHasItems && (
        <List className="*:p-4 *:first:pt-0">
          {summary.exchangeOrders.map((order) => (
            <ListItem key={order.id}>
              <ExchangeOrderLine order={order} />
            </ListItem>
          ))}
          {summary.warrantyReviews.map((warrantyReview) => (
            <ListItem key={warrantyReview.id}>
              <WarrantyReviewLine warrantyReview={warrantyReview} />
            </ListItem>
          ))}
          {summary.repairs.map((repair) => (
            <ListItem key={repair.id}>
              <WarrantyReviewLine warrantyReview={repair} isRepair />
            </ListItem>
          ))}
          {/* // TODO: fix the mb-2 */}
          {showMoneySection && (
            <ListItem key="summary-money-section">
              <div className="mb-2">
                <Card.Line>
                  <Badge className="w-fit">
                    <Icon icon={faBadgeDollar} />
                    <span>Money & Fees</span>
                  </Badge>
                  {showEditClaimFee && (
                    <div>
                      <Action
                        icon="edit"
                        variant="ghost"
                        accessibilityLabel="Edit Money & Fees"
                        onClick={() => setShowFeeForm(true)}
                      />
                      <EditClaimFee
                        show={showFeeForm}
                        onClose={() => setShowFeeForm(false)}
                      />
                    </div>
                  )}
                </Card.Line>
              </div>
              <ul className="space-y-2 py-2">
                {summary.giftCards.map((giftCard) => (
                  <li key={giftCard.id}>
                    <GiftCardLineItem giftCard={giftCard}>
                      {giftCard.platformAdminUrl && (
                        <AmountLineItem.Detail>
                          <Action
                            to={giftCard.platformAdminUrl}
                            target="_blank"
                            variant="link"
                            icon="shopify"
                            className="text-xs"
                          >
                            {giftCard.orderNameFromPlatform ?? 'View Order'}
                          </Action>
                        </AmountLineItem.Detail>
                      )}
                    </GiftCardLineItem>
                  </li>
                ))}
                {summary.refunds.map((refund) => (
                  <li key={refund.id}>
                    <RefundLineItem refund={refund} />
                  </li>
                ))}
                {summary.fees.map((fee) => (
                  <li key={fee.id}>
                    <FeeLineItem fee={fee} />
                  </li>
                ))}
                {claim.customerPayment && (
                  <li>
                    <PaymentLineItem payment={claim.customerPayment} />
                  </li>
                )}
              </ul>
            </ListItem>
          )}
        </List>
      )}
    </Card>
  );
}
